import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LogOut,
  Search,
  FileText,
  Calendar,
  Clock,
  Building2,
  FlaskConical,
  Eye,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  ShieldCheck,
  Receipt,
  Filter,
  X,
  Download,
  RefreshCw,
  Users,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stat Card Component - Now clickable
const StatCard = ({ icon: Icon, title, value, color, bgColor, onClick, isActive }) => (
  <div 
    className={`bg-white rounded-xl border shadow-sm p-5 cursor-pointer transition-all hover:shadow-md ${
      isActive ? 'border-[#505BB3] ring-2 ring-[#505BB3]/20' : 'border-slate-200'
    }`}
    onClick={onClick}
    data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl ${bgColor} flex items-center justify-center`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
    </div>
  </div>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    completada: 'bg-green-100 text-green-700 border-green-200',
    pendiente: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  };

  const icons = {
    completada: CheckCircle2,
    pendiente: AlertCircle,
  };

  const Icon = icons[status] || AlertCircle;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pendiente}`}>
      <Icon className="w-3.5 h-3.5" />
      {status === 'completada' ? 'Completada' : 'Pendiente'}
    </span>
  );
};

export default function AdminDashboard() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [filteredSolicitudes, setFilteredSolicitudes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [userPermisos, setUserPermisos] = useState(null);
  const navigate = useNavigate();
  
  // Filter states
  const [clinicas, setClinicas] = useState([]);
  const [selectedClinica, setSelectedClinica] = useState('todas');
  const [selectedEstado, setSelectedEstado] = useState('todos');
  const [selectedPeriodo, setSelectedPeriodo] = useState('30');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [activeStatFilter, setActiveStatFilter] = useState(null);

  // Permission helpers
  const canViewReportes = () => userPermisos?.reportes?.includes('ver');
  const canViewUsuarios = () => userPermisos?.usuarios?.includes('ver');

  // Periodo options
  const periodoOptions = [
    { value: '0', label: 'Hoy' },
    { value: '7', label: 'Últimos 7 días' },
    { value: '15', label: 'Últimos 15 días' },
    { value: '30', label: 'Últimos 30 días' },
    { value: '60', label: 'Últimos 60 días' },
    { value: '90', label: 'Últimos 90 días' },
    { value: 'todas', label: 'Todas' },
    { value: 'personalizado', label: 'Personalizado' },
  ];

  useEffect(() => {
    fetchSolicitudes();
    fetchClinicas();
    fetchUserPermisos();
  }, []);

  // Apply filters whenever filter states change
  useEffect(() => {
    applyFilters();
  }, [solicitudes, searchTerm, selectedClinica, selectedEstado, selectedPeriodo, fechaInicio, fechaFin, activeStatFilter]);

  const fetchUserPermisos = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/admin/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserPermisos(response.data.permisos);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
    }
  };

  const fetchClinicas = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/admin/clinicas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClinicas(response.data || []);
    } catch (error) {
      console.error('Error fetching clinicas:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...solicitudes];
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.patient_info?.nombre?.toLowerCase().includes(term) ||
          s.vet_info?.nombre?.toLowerCase().includes(term) ||
          s.id?.toLowerCase().includes(term) ||
          s.id_corto?.toLowerCase().includes(term)
      );
    }
    
    // Filter by clinica
    if (selectedClinica && selectedClinica !== 'todas') {
      filtered = filtered.filter((s) => s.clinica_id === selectedClinica);
    }
    
    // Filter by estado
    if (selectedEstado && selectedEstado !== 'todos') {
      filtered = filtered.filter((s) => s.status === selectedEstado);
    }
    
    // Filter by stat card click
    if (activeStatFilter) {
      const today = new Date().toISOString().split('T')[0];
      if (activeStatFilter === 'hoy') {
        filtered = filtered.filter((s) => s.fecha === today);
      } else if (activeStatFilter === 'pendientes') {
        filtered = filtered.filter((s) => s.status === 'pendiente');
      } else if (activeStatFilter === 'completadas') {
        filtered = filtered.filter((s) => s.status === 'completada');
      }
      // 'total' doesn't filter
    }
    
    // Filter by periodo
    if (selectedPeriodo && selectedPeriodo !== 'todas') {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      if (selectedPeriodo === 'personalizado') {
        if (fechaInicio) {
          filtered = filtered.filter((s) => s.fecha >= fechaInicio);
        }
        if (fechaFin) {
          filtered = filtered.filter((s) => s.fecha <= fechaFin);
        }
      } else {
        const days = parseInt(selectedPeriodo);
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - days);
        startDate.setHours(0, 0, 0, 0);
        const startDateStr = startDate.toISOString().split('T')[0];
        
        filtered = filtered.filter((s) => s.fecha >= startDateStr);
      }
    }
    
    setFilteredSolicitudes(filtered);
  };

  const handleStatCardClick = (filter) => {
    if (activeStatFilter === filter) {
      setActiveStatFilter(null); // Toggle off
    } else {
      setActiveStatFilter(filter);
      // Reset estado filter when clicking stat cards
      if (filter === 'pendientes') {
        setSelectedEstado('pendiente');
      } else if (filter === 'completadas') {
        setSelectedEstado('completada');
      } else {
        setSelectedEstado('todos');
      }
    }
  };

  const clearFilters = () => {
    setSelectedClinica('todas');
    setSelectedEstado('todos');
    setSelectedPeriodo('30');
    setFechaInicio('');
    setFechaFin('');
    setSearchTerm('');
    setActiveStatFilter(null);
  };

  const hasActiveFilters = selectedClinica !== 'todas' || selectedEstado !== 'todos' || 
    selectedPeriodo !== '30' || searchTerm || activeStatFilter;

  const handleExportFiltered = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // Build query params
      const params = new URLSearchParams();
      if (selectedClinica !== 'todas') params.append('clinica_id', selectedClinica);
      if (selectedEstado !== 'todos') params.append('status', selectedEstado);
      
      if (selectedPeriodo !== 'todas') {
        const today = new Date();
        if (selectedPeriodo === 'personalizado') {
          if (fechaInicio) params.append('fecha_inicio', fechaInicio);
          if (fechaFin) params.append('fecha_fin', fechaFin);
        } else {
          const days = parseInt(selectedPeriodo);
          const startDate = new Date(today);
          startDate.setDate(startDate.getDate() - days);
          params.append('fecha_inicio', startDate.toISOString().split('T')[0]);
        }
      }
      
      const response = await axios.get(`${API}/admin/solicitudes/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `solicitudes_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Solicitudes exportadas');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar solicitudes');
    }
  };

  const fetchSolicitudes = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/solicitudes`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSolicitudes(response.data);
      setFilteredSolicitudes(response.data);
    } catch (error) {
      console.error('Error fetching solicitudes:', error);
      if (error.response?.status === 401) {
        toast.error('Sesión expirada');
        handleLogout();
      } else {
        toast.error('Error al cargar solicitudes');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const stats = {
    total: solicitudes.length,
    today: solicitudes.filter((s) => s.fecha === new Date().toISOString().split('T')[0]).length,
    pending: solicitudes.filter((s) => s.status === 'pendiente').length,
    completed: solicitudes.filter((s) => s.status === 'completada').length,
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src="/amplicare-logo.svg" alt="Amplicare Lab" className="h-9" />
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/admin/clinicas')}
                variant="outline"
                className="h-10 border-slate-300"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Clínicas
              </Button>
              <Button
                onClick={() => navigate('/admin/catalogo')}
                variant="outline"
                className="h-10 border-slate-300"
              >
                <FlaskConical className="w-4 h-4 mr-2" />
                Catálogo
              </Button>
              {canViewReportes() && (
                <Button
                  onClick={() => navigate('/admin/reportes')}
                  variant="outline"
                  className="h-10 border-slate-300 bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                  data-testid="reportes-button"
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  Reportes
                </Button>
              )}
              {canViewUsuarios() && (
                <Button
                  onClick={() => navigate('/admin/usuarios')}
                  variant="outline"
                  className="h-10 border-slate-300 bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
                  data-testid="usuarios-button"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Usuarios
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleLogout}
                data-testid="logout-button"
                className="h-10 border-slate-300"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Admin Info Banner */}
      <div className="bg-[#505BB3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Panel de Administración</h1>
              <p className="text-white/80 text-sm">
                Bienvenido, {localStorage.getItem('admin_username') || 'Administrador'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={FileText}
            title="Total Solicitudes"
            value={stats.total}
            color="text-blue-600"
            bgColor="bg-blue-100"
            onClick={() => handleStatCardClick('total')}
            isActive={activeStatFilter === 'total'}
          />
          <StatCard
            icon={Calendar}
            title="Hoy"
            value={stats.today}
            color="text-purple-600"
            bgColor="bg-purple-100"
            onClick={() => handleStatCardClick('hoy')}
            isActive={activeStatFilter === 'hoy'}
          />
          <StatCard
            icon={AlertCircle}
            title="Pendientes"
            value={stats.pending}
            color="text-yellow-600"
            bgColor="bg-yellow-100"
            onClick={() => handleStatCardClick('pendientes')}
            isActive={activeStatFilter === 'pendientes'}
          />
          <StatCard
            icon={CheckCircle2}
            title="Completadas"
            value={stats.completed}
            color="text-green-600"
            bgColor="bg-green-100"
            onClick={() => handleStatCardClick('completadas')}
            isActive={activeStatFilter === 'completadas'}
          />
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-slate-600">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filtros:</span>
            </div>
            
            {/* Clínica Filter */}
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-400" />
              <Select value={selectedClinica} onValueChange={setSelectedClinica}>
                <SelectTrigger className="w-[180px] h-9" data-testid="filter-clinica">
                  <SelectValue placeholder="Todas las clínicas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas las clínicas</SelectItem>
                  {clinicas.map((clinica) => (
                    <SelectItem key={clinica.id} value={clinica.id}>
                      {clinica.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Estado Filter */}
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-slate-400" />
              <Select value={selectedEstado} onValueChange={(val) => { setSelectedEstado(val); setActiveStatFilter(null); }}>
                <SelectTrigger className="w-[140px] h-9" data-testid="filter-estado">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="completada">Completada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Periodo Filter */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <Select value={selectedPeriodo} onValueChange={setSelectedPeriodo}>
                <SelectTrigger className="w-[160px] h-9" data-testid="filter-periodo">
                  <SelectValue placeholder="Periodo" />
                </SelectTrigger>
                <SelectContent>
                  {periodoOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Custom Date Range */}
            {selectedPeriodo === 'personalizado' && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  className="h-9 w-[140px]"
                  placeholder="Desde"
                  data-testid="filter-fecha-inicio"
                />
                <span className="text-slate-400">-</span>
                <Input
                  type="date"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                  className="h-9 w-[140px]"
                  placeholder="Hasta"
                  data-testid="filter-fecha-fin"
                />
              </div>
            )}
            
            <div className="flex items-center gap-2 ml-auto">
              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-9 text-slate-600 hover:text-slate-900"
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpiar
                </Button>
              )}
              
              {/* Export Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportFiltered}
                className="h-9 text-green-600 border-green-200 hover:bg-green-50"
                data-testid="export-filtered"
              >
                <Download className="w-4 h-4 mr-1" />
                Exportar
              </Button>
            </div>
          </div>
          
          {/* Active filters badge */}
          {hasActiveFilters && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Filtros activos:</span>
                {selectedClinica !== 'todas' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                    <Building2 className="w-3 h-3" />
                    {clinicas.find(c => c.id === selectedClinica)?.nombre || 'Clínica'}
                  </span>
                )}
                {selectedEstado !== 'todos' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs">
                    {selectedEstado === 'pendiente' ? 'Pendiente' : 'Completada'}
                  </span>
                )}
                {selectedPeriodo !== 'todas' && selectedPeriodo !== '30' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                    <Calendar className="w-3 h-3" />
                    {periodoOptions.find(p => p.value === selectedPeriodo)?.label || selectedPeriodo}
                  </span>
                )}
                {searchTerm && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs">
                    <Search className="w-3 h-3" />
                    "{searchTerm}"
                  </span>
                )}
                <span className="text-xs text-slate-500 ml-2">
                  ({filteredSolicitudes.length} resultados)
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Solicitudes Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Table Header */}
          <div className="bg-[#505BB3] px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2 text-white">
                <FileText className="w-5 h-5" />
                <h2 className="text-lg font-semibold">Todas las Solicitudes</h2>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="search-input"
                  placeholder="Buscar por paciente, veterinario o ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-10 w-full sm:w-72 bg-white border-0 focus:ring-2 focus:ring-white/50"
                />
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="p-12 text-center text-slate-500">
                <div className="animate-spin w-8 h-8 border-2 border-[#505BB3] border-t-transparent rounded-full mx-auto mb-4"></div>
                Cargando solicitudes...
              </div>
            ) : filteredSolicitudes.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-1">
                  {searchTerm ? 'No se encontraron resultados' : 'Sin solicitudes'}
                </h3>
                <p className="text-slate-500">
                  {searchTerm
                    ? 'Intente con otros términos de búsqueda'
                    : 'Las solicitudes aparecerán aquí cuando se registren'}
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Paciente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Veterinario / Clínica
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Hora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Acción
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredSolicitudes.map((solicitud) => (
                    <tr
                      key={solicitud.id}
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`solicitud-row-${solicitud.id}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-sm text-slate-600 bg-slate-100 px-2 py-1 rounded">
                          {solicitud.id_corto || `SOL-${solicitud.id.slice(0, 6).toUpperCase()}`}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {solicitud.patient_info.nombre}
                          </div>
                          <div className="text-sm text-slate-500">
                            {solicitud.patient_info.especie} • {solicitud.patient_info.raza || 'Sin raza'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {solicitud.vet_info.nombre}
                          </div>
                          <div className="text-sm text-slate-500">
                            {solicitud.vet_info.clinica}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-sm text-slate-600">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {solicitud.fecha}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-sm text-slate-600">
                          <Clock className="w-4 h-4 text-slate-400" />
                          {solicitud.hora_toma}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={solicitud.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/solicitud/${solicitud.id}`)}
                          data-testid={`view-button-${solicitud.id}`}
                          className="border-[#505BB3] text-[#505BB3] hover:bg-[#505BB3] hover:text-white"
                        >
                          <Eye className="w-4 h-4 mr-1.5" />
                          Ver Detalles
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Table Footer */}
          {filteredSolicitudes.length > 0 && (
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Mostrando <span className="font-medium">{filteredSolicitudes.length}</span> de{' '}
                <span className="font-medium">{solicitudes.length}</span> solicitudes
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
// Force recompile Fri Dec 19 23:48:00 UTC 2025
